import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowRight,
  faClipboard,
  faClipboardCheck,
  faClock,
  faLifeRing,
  faListCheck,
  faPaperclip,
  faPlus,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import config from "../../config";
import { post } from "../../contexts/apicontexts";
import TaskComposerModal from "../Tasks/TasksModels/TaskComposerModal";
import TicketTaskListModal from "./TicketTaskListModal";
import { createTask, fetchEmployees, getTaskList, getTicketTaskCounts } from "../Tasks/taskService";
import {
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  isOwnTicketForIdentity,
  isCustomerAdminIdentity,
  isCustomerIdentity,
  isCustomerUserIdentity,
  isEmployeeIdentity,
  isStaffIdentity,
} from "./ticketUtils";
import {
  DEFAULT_TICKET_PAGE_SIZE,
  TICKET_PAGE_SIZE_OPTIONS,
  fetchAccessibleCustomers,
  fetchOrganizations,
  fetchTicketPagedList,
} from "./ticketService";

const BRAND_LR = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";


const emptyForm = (identity) => ({
  subject: "",
  domainUrl: "",
  category: "Technical",
  priority: "Medium",
  contactEmail: identity.email || "",
  userId: identity.userId || "",
  description: "",
  attachmentFiles: [],
});

const statusOrder = ["New", "In Progress", "On Hold", "Resolved"];
const MAX_VISIBLE_PAGES = 9;

export default function TicketsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const isCustomerView = useMemo(() => isCustomerIdentity(identity), [identity]);
  const isCustomerAdminView = useMemo(() => isCustomerAdminIdentity(identity), [identity]);
  const isCustomerUserView = useMemo(() => isCustomerUserIdentity(identity), [identity]);
  const isEmployeeView = useMemo(() => isEmployeeIdentity(identity), [identity]);
  const canOpenAdminRequest = useMemo(() => isStaffIdentity(identity), [identity]);
  const isAssignedRoute = location.pathname === "/assigned-tickets";
  const requestedUserId = useMemo(() => new URLSearchParams(location.search).get("userId") || identity.userId || "", [identity.userId, location.search]);
  const showAssignedView = isAssignedRoute || (isEmployeeView && !isCustomerView);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);

  const [tickets, setTickets] = useState([]);
  const [taskCountsByTicketId, setTaskCountsByTicketId] = useState({});
  const [employees, setEmployees] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [organizationFilter, setOrganizationFilter] = useState("All");
  const [customerFilter, setCustomerFilter] = useState("All");
  const [organizations, setOrganizations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TICKET_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    totalCount: 0, totalPages: 0, pageNumber: 1,
    pageSize: DEFAULT_TICKET_PAGE_SIZE, hasNextPage: false, hasPreviousPage: false,
  });
  const [summary, setSummary] = useState({
    total: 0, new: 0, inProgress: 0, onHold: 0, resolved: 0,
    myTicketCount: 0, unreadTicketCount: 0, unreadReplyCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [taskTicket, setTaskTicket] = useState(null);
  const [taskListTicket, setTaskListTicket] = useState(null);
  const [taskListItems, setTaskListItems] = useState([]);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [taskListError, setTaskListError] = useState("");
  const [formState, setFormState] = useState(() => emptyForm(identity));

  useEffect(() => { setPageNumber(1); }, [statusFilter, organizationFilter, customerFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchTicketPagedList({
        assignedView: showAssignedView,
        userId: requestedUserId,
        pageNumber, pageSize, searchText,
        status: statusFilter,
        organizationId: organizationFilter === "All" ? 0 : Number(organizationFilter),
        customerId: customerFilter === "All" ? 0 : Number(customerFilter),
      });
      setTickets(response.items || []);
      setPageMeta({
        totalCount: response.totalCount || 0,
        totalPages: response.totalPages || 0,
        pageNumber: response.pageNumber || 1,
        pageSize: response.pageSize || DEFAULT_TICKET_PAGE_SIZE,
        hasNextPage: Boolean(response.hasNextPage),
        hasPreviousPage: Boolean(response.hasPreviousPage),
      });
      setSummary(response.summary || {});
      setOrganizations((current) => (showAssignedView || isCustomerUserView || !current.length ? response.organizations || [] : current));
      setCustomers((current) => (showAssignedView || isCustomerUserView || !current.length ? response.customers || [] : current));
    } catch (loadError) {
      setTickets([]);
      setPageMeta((current) => ({ ...current, totalCount: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false }));
      setSummary({ total: 0, new: 0, inProgress: 0, onHold: 0, resolved: 0, myTicketCount: 0, unreadTicketCount: 0, unreadReplyCount: 0 });
      setError(loadError?.message || "Failed to load tickets.");
    } finally {
      setLoading(false);
    }
  }, [customerFilter, isCustomerUserView, organizationFilter, pageNumber, pageSize, requestedUserId, searchText, showAssignedView, statusFilter]);

  const fetchTaskCounts = React.useCallback(async () => {
    if (isCustomerView) { setTaskCountsByTicketId({}); return; }
    try {
      setTaskCountsByTicketId(await getTicketTaskCounts(showAssignedView ? { scopeUserId: requestedUserId } : {}));
    } catch { setTaskCountsByTicketId({}); }
  }, [isCustomerView, requestedUserId, showAssignedView]);

  useEffect(() => { fetchTickets(); fetchTaskCounts(); }, [fetchTickets, fetchTaskCounts]);

  useEffect(() => {
    let active = true;
    const loadFilterOptions = async () => {
      if (showAssignedView || isCustomerUserView) return;
      try {
        const [organizationItems, customerItems] = await Promise.all([
          canOpenAdminRequest ? fetchOrganizations() : Promise.resolve([]),
          canOpenAdminRequest ? fetchAccessibleCustomers("all") : fetchAccessibleCustomers("mine"),
        ]);
        if (!active) return;
        setOrganizations(canOpenAdminRequest ? organizationItems : []);
        setCustomers(customerItems);
      } catch {
        if (!active) return;
        setOrganizations([]); setCustomers([]);
      }
    };
    loadFilterOptions();
    return () => { active = false; };
  }, [canOpenAdminRequest, isCustomerUserView, showAssignedView]);

  useEffect(() => {
    if (isCustomerView) return;
    let cancelled = false;
    const loadEmployees = async () => {
      const items = await fetchEmployees();
      if (!cancelled) setEmployees(items);
    };
    loadEmployees();
    return () => { cancelled = true; };
  }, [isCustomerView]);

  const counts = useMemo(() => ({
    total: summary.total || 0,
    New: summary.new || 0,
    "In Progress": summary.inProgress || 0,
    "On Hold": summary.onHold || 0,
    Resolved: summary.resolved || 0,
  }), [summary]);

  const myTicketCount = summary.myTicketCount || 0;
  const unreadTicketCount = summary.unreadTicketCount || 0;
  const unreadReplyCount = summary.unreadReplyCount || 0;

  const visibleCustomers = useMemo(() => {
    if (organizationFilter === "All") return customers;
    return customers.filter((item) => Number(item.organizationId || 0) === Number(organizationFilter));
  }, [customers, organizationFilter]);

  useEffect(() => {
    if (customerFilter === "All") return;
    if (visibleCustomers.some((item) => String(item.id) === String(customerFilter))) return;
    setCustomerFilter("All");
  }, [customerFilter, visibleCustomers]);

  const paginationNumbers = useMemo(
    () => buildVisiblePageNumbers(pageMeta.pageNumber, pageMeta.totalPages, MAX_VISIBLE_PAGES),
    [pageMeta.pageNumber, pageMeta.totalPages]
  );

  const rangeLabel = useMemo(() => {
    if (!pageMeta.totalCount || !tickets.length) return "Showing 0 tickets";
    const start = (pageMeta.pageNumber - 1) * pageMeta.pageSize + 1;
    const end = start + tickets.length - 1;
    return `Showing ${start}–${end} of ${pageMeta.totalCount}`;
  }, [pageMeta.pageNumber, pageMeta.pageSize, pageMeta.totalCount, tickets.length]);

  const resetForm = () => { setFormState(emptyForm(identity)); setFormErrors({}); setError(""); };

  const validateForm = () => {
    const nextErrors = {};
    if (!formState.subject.trim()) nextErrors.subject = "Subject is required.";
    if (!formState.category) nextErrors.category = "Category is required.";
    if (!formState.priority) nextErrors.priority = "Priority is required.";
    if (!formState.contactEmail.trim()) nextErrors.contactEmail = "Contact email is required.";
    else if (!/\S+@\S+\.\S+/.test(formState.contactEmail.trim())) nextErrors.contactEmail = "Contact email is invalid.";
    if (!formState.userId.trim()) nextErrors.userId = "Logged in user ID is missing.";
    if (!formState.description.trim()) nextErrors.description = "Description is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateTicket = async () => {
    if (!validateForm()) return;
    setSaving(true); setError("");
    const formData = new FormData();
    formData.append("Subject", formState.subject.trim());
    formData.append("DomainUrl", formState.domainUrl.trim());
    formData.append("Category", formState.category);
    formData.append("Priority", formState.priority);
    formData.append("ContactEmail", formState.contactEmail.trim());
    formData.append("UserId", formState.userId.trim());
    formData.append("Description", formState.description.trim());
    (formState.attachmentFiles || []).forEach((file) => formData.append("Files", file));
    try {
      const response = await post(`${config.baseurl}Ticket/add`, formData);
      if (response?.status === 200) {
        setIsCreateOpen(false); resetForm(); await fetchTickets();
        navigate(`/tickets/${response.data.id}`, { state: { backTo: "/tickets" } });
        return;
      }
      setError(response?.message || "Failed to create ticket.");
    } catch { setError("Failed to create ticket."); }
    finally { setSaving(false); }
  };

  const openTaskComposer = (ticket, options = {}) => {
    if (!options.skipConfirm && !window.confirm(`Create a task from ticket ${ticket.ticketNumber || ""}?`)) return;
    setTaskTicket(ticket);
  };

  const closeTaskListModal = () => { setTaskListTicket(null); setTaskListItems([]); setTaskListLoading(false); setTaskListError(""); };

  const openTaskListModal = async (ticket) => {
    const ticketId = Number(ticket?.id || 0);
    setTaskListTicket(ticket); setTaskListItems([]); setTaskListLoading(true); setTaskListError("");
    try { setTaskListItems(await getTaskList({ ticketId })); }
    catch { setTaskListError("Ticket task list could not be loaded."); }
    finally { setTaskListLoading(false); }
  };

  const handleCreateTask = async (values) => {
    if (!taskTicket) return;
    setTaskSaving(true); setError("");
    try {
      await createTask({
        ...values,
        ticketId: Number(taskTicket.id || 0),
        ticketNumber: taskTicket.ticketNumber || "",
        ticketSubject: taskTicket.subject || "",
        ticketStatus: taskTicket.status || "",
        sourceType: "SupportTicket",
        createdByUserId: identity.userId,
        createdByName: identity.fullName || identity.email || "User",
      }, identity);
      setTaskTicket(null); await fetchTaskCounts();
      if (taskListTicket && Number(taskListTicket.id || 0) === Number(taskTicket.id || 0)) {
        setTaskListItems(await getTaskList({ ticketId: Number(taskTicket.id || 0) }));
      }
    } catch { setError("Task could not be created from this ticket."); }
    finally { setTaskSaving(false); }
  };

  return (
    <div className="min-h-screen p-3 md:p-5 bg-gray-50 dark:bg-[#0F111A]">
      <Breadcrumb
        pageName={showAssignedView ? "Assigned Tickets" : "Support Tickets"}
        currentPage={showAssignedView ? "Assigned Tickets" : "Support Tickets"}
      />

      <div className="mt-4 overflow-hidden rounded-3xl border shadow-xl bg-white dark:bg-[#161B22] dark:border-gray-800 border-orange-100" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>

        {/* ── Premium Hero Header ── */}
        <div className="relative overflow-hidden text-white" style={{ background: BRAND_135 }}>
          {/* decorative blobs */}
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute -left-8 bottom-0 h-32 w-32 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.10)" }} />
          <div className="pointer-events-none absolute right-1/3 top-0 h-20 w-40 rounded-full blur-2xl" style={{ background: "rgba(255,255,255,0.04)" }} />

          {/* top accent line */}
          <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />

          <div className="relative px-5 pb-5 pt-5 sm:px-7">
            {/* ── Row 1: label + buttons ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {/* eyebrow */}
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-100 backdrop-blur-sm">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20">
                    <FontAwesomeIcon icon={faLifeRing} className="text-[8px]" />
                  </span>
                  {showAssignedView ? "Assigned Ticket Desk" : isCustomerView ? "Customer Support" : "Support Desk"}
                </div>

                {/* headline */}
                <h1 className="mt-2 text-xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-2xl">
                  {showAssignedView ? "Assigned Tickets" : isCustomerView ? "My Support Tickets" : "Support Ticket Inbox"}
                </h1>
                {(showAssignedView || isCustomerView) ? (
                  <p className="mt-0.5 text-[11px] text-orange-200">
                    {showAssignedView
                      ? "Tickets routed to your account — review, update, and resolve."
                      : "Track every request you've raised and watch for staff replies."}
                  </p>
                ) : null}
                {unreadReplyCount ? (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/30 bg-emerald-400/15 px-2.5 py-0.5 text-[10px] font-bold text-emerald-100">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />
                      {unreadReplyCount} unread {unreadReplyCount === 1 ? "update" : "updates"}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* action buttons — top-right */}
              <div className="flex shrink-0 flex-wrap items-start gap-2 sm:justify-end">
                {!showAssignedView && isCustomerView ? (
                  <button
                    type="button"
                    onClick={() => { resetForm(); setIsCreateOpen(true); }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/15 px-3.5 py-2 text-xs font-bold text-white shadow-sm backdrop-blur-sm transition hover:bg-white/25"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    New Ticket
                  </button>
                ) : null}
                {!isCustomerView ? (
                  <button
                    type="button"
                    onClick={() => navigate("/my-tasks")}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faListCheck} />
                    Open Tasks
                  </button>
                ) : null}
                {!showAssignedView && canOpenAdminRequest ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate("/tickets/admin/new-request")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/15 px-3.5 py-2 text-xs font-bold text-white shadow-sm backdrop-blur-sm transition hover:bg-white/25"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Support Request
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/tickets/admin-created")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faClipboard} />
                      Admin Tickets
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => { fetchTickets(); fetchTaskCounts(); }}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3.5 py-2 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/20"
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                  Refresh
                </button>
              </div>
            </div>

            {/* ── Row 2: stat cards ── */}
            <div className="mt-4 grid grid-cols-5 gap-1.5">
              {[
                { label: "Total", value: counts.total, key: "All", icon: faTicket, bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.18)" },
                { label: "New", value: counts["New"], key: "New", icon: faPlus, bg: "rgba(96,165,250,0.18)", border: "rgba(96,165,250,0.35)" },
                { label: "Progress", value: counts["In Progress"], key: "In Progress", icon: faClock, bg: "rgba(251,191,36,0.18)", border: "rgba(251,191,36,0.35)" },
                { label: "On Hold", value: counts["On Hold"], key: "On Hold", icon: faSpinner, bg: "rgba(251,146,60,0.18)", border: "rgba(251,146,60,0.35)" },
                { label: "Resolved", value: counts["Resolved"], key: "Resolved", icon: faClipboardCheck, bg: "rgba(52,211,153,0.18)", border: "rgba(52,211,153,0.35)" },
              ].map(({ label, value, key, icon, bg, border }) => {
                const isActive = statusFilter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatusFilter(key)}
                    className="group relative overflow-hidden rounded-xl border px-2 py-2 text-left transition-all hover:scale-[1.02]"
                    style={{
                      background: isActive ? "rgba(255,255,255,0.22)" : bg,
                      borderColor: isActive ? "rgba(255,255,255,0.6)" : border,
                      boxShadow: isActive ? "0 0 0 2px rgba(255,255,255,0.25)" : "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[8px] font-bold uppercase tracking-[0.16em] text-orange-100 leading-tight">{label}</span>
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-md"
                        style={{ background: isActive ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.15)" }}
                      >
                        <FontAwesomeIcon icon={icon} className="text-[7px] text-white" />
                      </span>
                    </div>
                    <div className="mt-1 text-xl font-extrabold leading-none tracking-tight text-white">
                      {value}
                    </div>
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-xl bg-white/50" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:px-6 border-orange-100 bg-gradient-to-r from-orange-50/60 to-red-50/40 dark:bg-[#161B22] dark:border-gray-800 dark:bg-none">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border px-3 py-2 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
            onBlur={(e) => { e.target.style.borderColor = ""; }}
          >
            <option value="All">All Status</option>
            {statusOrder.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          {canOpenAdminRequest && (
            <select
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
              onBlur={(e) => { e.target.style.borderColor = ""; }}
            >
              <option value="All">All Organizations</option>
              {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}

          {canOpenAdminRequest && (
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
              onBlur={(e) => { e.target.style.borderColor = ""; }}
            >
              <option value="All">All Customers</option>
              {visibleCustomers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}

          <div className="relative flex-1 min-w-[200px]">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm" style={{ color: "#C04C2A" }} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search subject, requester, customer…"
              className="w-full rounded-xl border py-2 pl-9 pr-3 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
              onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
            />
          </div>

          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value) || DEFAULT_TICKET_PAGE_SIZE); setPageNumber(1); }}
            className="rounded-xl border px-3 py-2 text-sm outline-none transition border-orange-100 bg-white text-slate-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
            onBlur={(e) => { e.target.style.borderColor = ""; }}
          >
            {TICKET_PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={o}>Page Size: {o}</option>)}
          </select>

          <span className="ml-auto text-xs font-medium text-slate-400">{rangeLabel}</span>
        </div>

        {/* ── Content ── */}
        <div className="p-4 sm:p-5">
          {!showAssignedView && isCustomerView ? (
            <div className="mb-3 flex flex-wrap gap-2 text-xs font-medium">
              <span className="rounded-full border px-3 py-1 border-orange-100 bg-orange-50 text-slate-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300">My tickets: {myTicketCount}</span>
              <span className="rounded-full border px-3 py-1 border-orange-100 bg-orange-50 text-slate-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300">All accessible: {tickets.length}</span>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">Needs attention: {unreadTicketCount}</span>
            </div>
          ) : null}

          {error ? (
            <div className="mb-3 rounded-xl border px-4 py-2.5 text-sm border-rose-200 bg-rose-50 text-rose-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed px-6 py-14 text-center text-sm border-orange-200 bg-orange-50/30 dark:bg-gray-800/30 dark:border-gray-700" style={{ color: "#C04C2A" }}>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading tickets…
            </div>
          ) : tickets.length ? (
            <div className="space-y-2.5">
              {tickets.map((ticket) => {
                const taskCount = taskCountsByTicketId[Number(ticket.id || 0)] || 0;
                const isOwnTicket = isOwnTicketForIdentity(ticket, identity);
                const unreadLabel = ticket.unreadCount === 1 ? "1 unread update" : `${ticket.unreadCount} unread updates`;
                return (
                  <div
                    key={ticket.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/tickets/${ticket.id}`, { state: { backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets" } })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/tickets/${ticket.id}`, { state: { backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets" } });
                      }
                    }}
                    className={`group relative w-full cursor-pointer overflow-hidden rounded-2xl border text-left transition-colors hover:shadow-md ${ticket.hasUnread ? "bg-amber-50/30 dark:bg-amber-950/20 hover:bg-amber-50/50 dark:hover:bg-amber-950/30" : "bg-white dark:bg-[#161B22] hover:bg-gray-50 dark:hover:bg-gray-800"}`}
                    style={{
                      borderColor: ticket.hasUnread ? "#fcd34d" : "rgba(192,76,42,0.12)",
                    }}
                  >
                    {/* left accent bar */}
                    <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: ticket.hasUnread ? "#fbbf24" : BRAND_LR }} />

                    <div className="flex flex-col gap-3 p-3 pl-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                          <span className="rounded-lg border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border-slate-200 bg-slate-100 text-slate-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                            {ticket.ticketNumber}
                          </span>
                          <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getStatusClasses(ticket.status)}`}>{ticket.status}</span>
                          <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${getPriorityClasses(ticket.priority)}`}>{ticket.priority}</span>
                                          {!isCustomerView && !isCustomerUserView && ticket.organizationName ? (
                            <span className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700">{ticket.organizationName}</span>
                          ) : null}
                          {!isCustomerView && !isCustomerUserView && ticket.customerName ? (
                            <span className="rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[10px] font-bold text-cyan-700">{ticket.customerName}</span>
                          ) : null}
                          {isCustomerView ? (
                            <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${isOwnTicket ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "border border-slate-200 bg-slate-100 text-slate-500"}`}>
                              {isOwnTicket ? "My ticket" : "Shared"}
                            </span>
                          ) : null}
                          {ticket.hasUnread ? (
                            <span className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">{unreadLabel}</span>
                          ) : null}
                          {isCustomerUserView && !ticket.canReply ? (
                            <span className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">View only</span>
                          ) : isCustomerUserView ? (
                            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Reply allowed</span>
                          ) : null}
                          {!isCustomerView ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openTaskListModal(ticket); }}
                              className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 transition hover:bg-blue-100"
                            >
                              {taskCount} {taskCount === 1 ? "Task" : "Tasks"}
                            </button>
                          ) : null}
                        </div>
                        <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-white">{ticket.subject}</h3>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400">
                          <span>{ticket.category}</span>
                          {!isCustomerView && !isCustomerUserView && <span>{ticket.contactEmail}</span>}
                          <span>{ticket.domainUrl || "No domain"}</span>
                        </div>
                        {!isCustomerView && !isCustomerUserView && (
                          <div className="mt-1.5 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                            <span>Requester: {ticket.requesterName || ticket.userName || "—"}</span>
                            {ticket.customerName ? <span>Customer: {ticket.customerName}</span> : null}
                            {ticket.organizationName ? <span>Org: {ticket.organizationName}</span> : null}
                          </div>
                        )}
                      </div>

                      <div className="shrink-0 text-right">
                        {ticket.unreadCount > 0 && (
                          <span className="mb-1.5 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white" style={{ background: "linear-gradient(135deg,#ef4444,#b91c1c)" }}>
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/80" />
                            {ticket.unreadCount} unread
                          </span>
                        )}
                        <div className="text-[11px] text-slate-400">Opened {formatDateTime(ticket.createdAt)}</div>
                        <div className="text-[11px] text-slate-400">Updated {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}</div>
                        <div className="mt-2 flex flex-wrap justify-end gap-1.5">
                          {!isCustomerView ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openTaskComposer(ticket); }}
                              className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition border-orange-100 bg-orange-50 text-slate-700 hover:bg-orange-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <FontAwesomeIcon icon={faClipboardCheck} className="text-[10px]" style={{ color: "#C04C2A" }} />
                              Make Task
                            </button>
                          ) : null}
                          <Link
                            to={`/tickets/${ticket.id}?${new URLSearchParams({ backTo: isAssignedRoute ? `${location.pathname}${location.search}` : "/tickets" })}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-white transition hover:opacity-90"
                            style={{ background: BRAND_LR }}
                          >
                            Open <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed px-6 py-14 text-center" style={{ borderColor: "rgba(192,76,42,0.22)", background: "rgba(192,76,42,0.02)" }}>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND_LR }}>
                <FontAwesomeIcon icon={faTicket} />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">No tickets found</h3>
              <p className="mt-1.5 text-sm text-slate-500 dark:text-gray-400">
                {showAssignedView
                  ? "No customer tickets are assigned to your login right now."
                  : isCustomerUserView
                    ? "Your customer ticket list is empty right now."
                    : isCustomerAdminView
                      ? "No ticket matches the selected customer filters."
                      : "Create your first ticket to start the support conversation."}
              </p>
              {!showAssignedView && isCustomerView ? (
                <button
                  type="button"
                  onClick={() => { resetForm(); setIsCreateOpen(true); }}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white"
                  style={{ background: BRAND_LR }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New Ticket
                </button>
              ) : null}
            </div>
          )}

          {/* Pagination */}
          {tickets.length ? (
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 lg:flex-row lg:items-center lg:justify-between border-orange-100 dark:border-gray-800">
              <span className="text-xs text-slate-400">{rangeLabel}</span>
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPageNumber(1)}
                  disabled={!pageMeta.hasPreviousPage}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-40 border-orange-100 bg-white text-slate-500 hover:bg-orange-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FontAwesomeIcon icon={faAnglesLeft} className="text-xs" />
                </button>
                {paginationNumbers.map((page) => (
                  page === pageMeta.pageNumber ? (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setPageNumber(page)}
                      className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-xl border px-2 text-xs font-bold transition"
                      style={{ background: BRAND_LR, borderColor: "transparent", color: "#fff" }}
                    >
                      {page}
                    </button>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setPageNumber(page)}
                      className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-xl border px-2 text-xs font-bold transition border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                    >
                      {page}
                    </button>
                  )
                ))}
                <button
                  type="button"
                  onClick={() => setPageNumber(pageMeta.totalPages || 1)}
                  disabled={!pageMeta.hasNextPage}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-40 border-orange-100 bg-white text-slate-500 hover:bg-orange-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FontAwesomeIcon icon={faAnglesRight} className="text-xs" />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Create Ticket Modal ── */}
      {isCreateOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-3">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsCreateOpen(false)} />
          <div
            className="relative z-10 flex w-full flex-col overflow-hidden shadow-2xl bg-white dark:bg-gray-900"
            style={{ maxWidth: 640, maxHeight: "96dvh", borderRadius: "1.25rem 1.25rem 0 0" }}
          >
            {/* Modal Header */}
            <div className="relative shrink-0 px-4 py-4 text-white sm:px-6" style={{ background: BRAND_135 }}>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-orange-100">
                <FontAwesomeIcon icon={faLifeRing} />
                Create Ticket
              </div>
              <h3 className="mt-1.5 pr-8 text-base font-extrabold tracking-tight text-white sm:text-lg">Open a new support request</h3>
              <p className="mt-0.5 text-[11px] text-orange-200">Created under your logged-in customer account.</p>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-2">
                <Field label="Subject" required error={formErrors.subject}>
                  <input
                    value={formState.subject}
                    onChange={(e) => setFormState((c) => ({ ...c, subject: e.target.value }))}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-gray-500"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                    placeholder="Example: Billing issue on renewal invoice"
                  />
                </Field>

                <Field label="Domain URL">
                  <input
                    value={formState.domainUrl}
                    onChange={(e) => setFormState((c) => ({ ...c, domainUrl: e.target.value }))}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-gray-500"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                    placeholder="https://example.com"
                  />
                </Field>

                <Field label="Category" required error={formErrors.category}>
                  <select
                    value={formState.category}
                    onChange={(e) => setFormState((c) => ({ ...c, category: e.target.value }))}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; }}
                  >
                    <option value="Billing">Billing</option>
                    <option value="Technical">Technical</option>
                    <option value="General">General</option>
                  </select>
                </Field>

                <Field label="Priority" required error={formErrors.priority}>
                  <select
                    value={formState.priority}
                    onChange={(e) => setFormState((c) => ({ ...c, priority: e.target.value }))}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                    onBlur={(e) => { e.target.style.borderColor = ""; }}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </Field>

                <Field label="Contact Email" required error={formErrors.contactEmail}>
                  <input
                    value={formState.contactEmail}
                    onChange={(e) => setFormState((c) => ({ ...c, contactEmail: e.target.value }))}
                    readOnly={Boolean(identity.email)}
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 read-only:bg-slate-50 dark:bg-gray-800 dark:border-gray-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-gray-500 dark:read-only:bg-gray-900"
                    onFocus={(e) => { if (!identity.email) { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; } }}
                    onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                    placeholder="customer@example.com"
                  />
                </Field>

                <Field label="User ID" required error={formErrors.userId}>
                  <input
                    value={formState.userId}
                    readOnly
                    className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none border-slate-200 bg-slate-50 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="Description" required error={formErrors.description}>
                    <textarea
                      value={formState.description}
                      onChange={(e) => setFormState((c) => ({ ...c, description: e.target.value }))}
                      className="min-h-[120px] w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition border-slate-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-gray-500"
                      onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; e.target.style.boxShadow = "0 0 0 3px rgba(192,76,42,0.10)"; }}
                      onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
                      placeholder="Describe the issue, error steps, and anything support should know."
                    />
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="File Attachments">
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-5 text-center transition border-orange-200 bg-orange-50/30 hover:bg-orange-50 dark:bg-gray-800/30 dark:border-gray-700 dark:hover:bg-gray-800/50">
                      <FontAwesomeIcon icon={faPaperclip} className="mb-1.5 text-[#C04C2A]" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-gray-200">Click to attach files</span>
                      <span className="mt-0.5 text-xs text-slate-400">Screenshots, invoices, logs — multiple files allowed</span>
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const picked = Array.from(e.target.files || []);
                          setFormState((c) => ({ ...c, attachmentFiles: [...(c.attachmentFiles || []), ...picked] }));
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {(formState.attachmentFiles || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {formState.attachmentFiles.map((file, idx) => (
                          <li key={idx} className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                            <FontAwesomeIcon icon={faPaperclip} className="shrink-0 text-[#C04C2A]" />
                            <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">{file.name}</span>
                            <span className="shrink-0 text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                            <button
                              type="button"
                              onClick={() => setFormState((c) => ({ ...c, attachmentFiles: c.attachmentFiles.filter((_, i) => i !== idx) }))}
                              className="shrink-0 text-slate-400 hover:text-red-500 transition"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Field>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex shrink-0 flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:justify-end sm:px-5 border-slate-100 bg-white dark:bg-gray-900 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="w-full rounded-xl border px-4 py-2.5 text-sm font-bold transition sm:w-auto border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateTicket}
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:opacity-70 sm:w-auto"
                style={{ background: BRAND_LR }}
              >
                <FontAwesomeIcon icon={saving ? faSpinner : faPlus} spin={saving} />
                {saving ? "Creating…" : "Create Ticket"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TaskComposerModal
        open={Boolean(taskTicket)}
        ticket={taskTicket}
        employees={employees}
        identity={identity}
        submitting={taskSaving}
        onClose={() => setTaskTicket(null)}
        onSubmit={handleCreateTask}
      />

      <TicketTaskListModal
        open={Boolean(taskListTicket)}
        ticket={taskListTicket}
        tasks={taskListItems}
        loading={taskListLoading}
        error={taskListError}
        onClose={closeTaskListModal}
        onCreateTask={() => {
          if (!taskListTicket) return;
          closeTaskListModal();
          openTaskComposer(taskListTicket, { skipConfirm: true });
        }}
      />
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? <div className="mt-1.5 text-[11px] font-medium text-rose-600">{error}</div> : null}
    </div>
  );
}

function buildVisiblePageNumbers(currentPage, totalPages, maxVisiblePages) {
  if (!totalPages) return [];
  const safeMax = Math.max(1, maxVisiblePages);
  let startPage = Math.max(1, currentPage - Math.floor(safeMax / 2));
  let endPage = startPage + safeMax - 1;
  if (endPage > totalPages) { endPage = totalPages; startPage = Math.max(1, endPage - safeMax + 1); }
  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) pages.push(page);
  return pages;
}
